import { ArrowRight, Gift, FileText, Video, MessageCircle, GraduationCap, Heart, Shield } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { PURCHASE_URL, BONUSES, PRODUCT_IMAGES } from '../../data/mock';
import { trackPurchaseClick } from '../../utils/pixel';

const handlePurchase = () => {
  trackPurchaseClick();
  window.open(PURCHASE_URL, '_blank', 'noopener,noreferrer');
};

const bonusIcons = [<FileText size={20} />, <Video size={20} />, <MessageCircle size={20} />, <GraduationCap size={20} />];

export const OfferSection = () => {
  const [ref1, vis1] = useScrollReveal();
  const [ref2, vis2] = useScrollReveal();
  const [ref3, vis3] = useScrollReveal();
  const [ref4, vis4] = useScrollReveal();

  return (
    <>
      {/* Authority / Story */}
      <section className="py-20 md:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, rgba(253,242,248,0.7) 0%, rgba(252,231,243,0.6) 50%, rgba(251,207,232,0.5) 100%)' }}>
        <div className="absolute top-10 right-0 w-[350px] h-[350px] rounded-full opacity-12" style={{ background: 'radial-gradient(circle, #FBCFE8 0%, transparent 70%)' }} />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div ref={ref1} className={`reveal ${vis1 ? 'visible' : ''}`}>
            <div className="grid lg:grid-cols-5 gap-12 items-center">
              {/* Product image - clean */}
              <div className="lg:col-span-2 flex justify-center">
                <div className="perspective-container">
                  <div className="product-3d relative">
                    <img src={PRODUCT_IMAGES.secondary} alt="Manual Cejas Radiantes" className="w-full max-w-xs rounded-2xl" style={{ objectFit: 'cover' }} />
                  </div>
                </div>
              </div>

              {/* Story text */}
              <div className="lg:col-span-3 space-y-6">
                <Badge className="mb-2 font-body text-xs rounded-full px-4 py-1.5 border-0" style={{ backgroundColor: 'rgba(252,231,243,0.9)', color: '#9D174D' }}>Mi Historia</Badge>
                
                <div className="space-y-4 font-body text-base leading-relaxed" style={{ color: '#3D2E50' }}>
                  <p>
                    Hoy es muy común aprender por partes:
                    un video aquí, una técnica allá, un tip rápido que parece útil.
                    Y aunque cada pieza aporta algo, no siempre muestran cómo se conecta todo.
                  </p>
                  <div className="glass-strong rounded-2xl p-5">
                    <p>
                      Cuando el aprendizaje es fragmentado, la práctica también lo es.
                      Sabes hacer pasos. Lo que falta es ver cómo esos pasos se sostienen entre sí.
                    </p>
                  </div>
                  <p>
                    Cuando no ves la estructura completa, cada decisión vuelve a sentirse individual.
                    Y ahí es donde la duda aparece.
                  </p>
                  <div className="glass-pink rounded-2xl p-5">
                    <p className="font-display text-lg italic" style={{ color: '#9D174D' }}>
                      Cuando entiendes cómo todo se conecta, la experiencia de diseñar cambia.
                    </p>
                  </div>
                  <p>
                    Este manual nace de mi propio proceso.
                    Durante más de dos años estudié, practiqué y perfeccioné el diseño, visagismo, pigmentación y depilación de cejas a través de formación especializada.
                  </p>
                  <p className="font-semibold" style={{ color: '#831843' }}>
                    Y algo se hizo evidente:
                    No hacía falta más información.
                    Hacía falta una estructura que sostuviera cada decisión.
                  </p>
                  <p>
                    Por eso reuní todo en un manual directo, organizado y sin rodeos.
                    Para que cualquier mujer pueda diseñar con mayor tranquilidad y seguridad entendiendo cada paso que da.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing + Bonuses */}
      <section id="oferta" className="py-20 md:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #FDF2F8 0%, #FCE7F3 30%, #FBCFE8 60%, #FCE7F3 100%)' }}>
        <div className="absolute top-10 right-0 w-[400px] h-[400px] rounded-full blob opacity-15" style={{ background: 'radial-gradient(circle, #FBCFE8 0%, transparent 70%)' }} />
        <div className="absolute bottom-10 left-0 w-[350px] h-[350px] rounded-full blob-delay opacity-10" style={{ background: 'radial-gradient(circle, #F9A8D4 0%, transparent 70%)' }} />

        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div ref={ref2} className={`reveal-scale ${vis2 ? 'visible' : ''}`}>
            {/* Pricing Card */}
            <div className="glass-strong rounded-3xl p-8 md:p-12 shimmer-border mb-16">
              <div className="text-center space-y-6 font-body leading-relaxed" style={{ color: '#3D2E50' }}>
                <p className="text-lg">
                  Hoy puedes acceder al <span className="font-bold" style={{ color: '#9D174D' }}>Manual Cejas Radiantes</span> por
                </p>
                <div className="py-6">
                  <span className="font-display text-7xl md:text-8xl font-bold gradient-text">$14,99</span>
                </div>
                <p className="text-lg">Una decisión pequeña frente a lo que estás construyendo.</p>

                <Separator className="my-6" style={{ backgroundColor: 'rgba(244, 114, 182, 0.15)' }} />

                <div className="grid sm:grid-cols-2 gap-4 text-left my-8">
                  {[
                    { label: 'Cada diseño sin estructura', value: 'refuerza la duda.' },
                    { label: 'Cada diseño con sistema', value: 'fortalece tu certeza.', highlight: true }
                  ].map((item, i) => (
                    <div key={i} className={`rounded-2xl p-5 ${item.highlight ? 'glass-pink' : 'glass'}`}>
                      <p className="font-body text-sm font-semibold mb-1" style={{ color: item.highlight ? '#9D174D' : '#4A3B5C' }}>{item.label}</p>
                      <p className="font-display text-lg font-bold" style={{ color: item.highlight ? '#BE185D' : '#3D2E50' }}>{item.value}</p>
                    </div>
                  ))}
                </div>

                <p className="text-base">
                  No estás comprando información.
                  Estás invirtiendo en estabilidad futura.
                  En trabajar con fundamento. En terminar con tranquilidad. En transmitir seguridad real.
                </p>

                <p className="text-base">
                  Y cuando la seguridad se transmite, la elección deja de ser casual.
                  Se vuelve continuidad:
                  <span className="font-bold" style={{ color: '#9D174D' }}> Cada clienta vuelve a ti.</span>
                </p>

                <Separator className="my-6" style={{ backgroundColor: 'rgba(244, 114, 182, 0.15)' }} />

                <p className="text-base">
                  Ahora, la diferencia no está en cuándo decides acceder al manual.
                  <span className="font-semibold" style={{ color: '#831843' }}> Está en cuándo decides dejar de practicar con inseguridad.</span>
                </p>
              </div>
            </div>
          </div>

          {/* Bonuses */}
          <div ref={ref3} className={`stagger-children ${vis3 ? 'visible' : ''}`}>
            <div className="text-center mb-10">
              <Badge className="mb-4 font-body text-xs rounded-full px-4 py-1.5 border-0" style={{ backgroundColor: 'rgba(252,231,243,0.9)', color: '#9D174D' }}>
                <Gift size={12} className="mr-1" /> Si tomas esa decisión HOY
              </Badge>
              <p className="font-body text-lg" style={{ color: '#3D2E50' }}>
                Además del manual, este acceso incluye recursos adicionales que fortalecen tu proceso mientras desarrollas tu seguridad.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              {BONUSES.map((bonus, idx) => (
                <div key={idx} className="glass-strong rounded-2xl p-6 hover-lift shimmer-border">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(252,231,243,0.9)', color: '#DB2777' }}>
                      {bonusIcons[idx]}
                    </div>
                    <h4 className="font-display text-base font-bold" style={{ color: '#1F1235' }}>{bonus.title}</h4>
                  </div>
                  <p className="font-body text-sm leading-relaxed mb-4" style={{ color: '#4A3B5C' }}>{bonus.description}</p>
                  <Badge variant="outline" className="font-body text-xs rounded-full" style={{ borderColor: '#F9A8D4', color: '#BE185D' }}>{bonus.tag}</Badge>
                </div>
              ))}
            </div>

            <p className="font-body text-sm text-center mt-8 italic" style={{ color: '#9D8BA7' }}>
              Estos recursos están disponibles dentro de esta edición actual.
              Más adelante podrían reorganizarse o venderse por separado.
            </p>

            <div className="text-center mt-10 space-y-4">
              <p className="font-body text-lg" style={{ color: '#3D2E50' }}>
                Si sientes que es momento de dejar de practicar con duda
                y estás lista para empezar a diseñar con estructura y certeza,
                puedes acceder al manual aquí.
              </p>
              <p className="font-display text-lg font-bold" style={{ color: '#9D174D' }}>
                Empieza a construir tu estabilidad desde el próximo diseño.
              </p>
              <Button onClick={handlePurchase} size="lg" className="cta-pulse rounded-full text-white font-body font-bold text-base px-10 py-6 h-auto mt-4" style={{ backgroundColor: '#DB2777' }}>
                Acceder al Manual
                <ArrowRight size={18} className="ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Secondary CTA */}
      <section className="py-16 md:py-20 text-center relative overflow-hidden" style={{ background: 'linear-gradient(180deg, rgba(252,231,243,0.6) 0%, rgba(253,242,248,0.8) 100%)' }}>
        <div className="max-w-3xl mx-auto px-6">
          <p className="font-body text-lg mb-2" style={{ color: '#3D2E50' }}>
            Accede hoy al <span className="font-bold" style={{ color: '#9D174D' }}>Manual Cejas Radiantes</span>
          </p>
          <p className="font-body text-lg mb-8" style={{ color: '#3D2E50' }}>
            y consolida tu seguridad durante los próximos 2 meses mientras aplicas cada diseño con confirmación directa.
          </p>
          <Button onClick={handlePurchase} size="lg" className="cta-pulse rounded-full text-white font-body font-bold text-base px-10 py-6 h-auto" style={{ backgroundColor: '#DB2777' }}>
            Acceder Ahora
            <ArrowRight size={18} className="ml-1" />
          </Button>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 md:py-32 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #FDF2F8 0%, #FCE7F3 30%, #FBCFE8 60%, #FCE7F3 100%)' }}>
        <div className="absolute top-0 right-0 w-[450px] h-[450px] rounded-full blob opacity-18" style={{ background: 'radial-gradient(circle, #F472B6 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full blob-delay opacity-12" style={{ background: 'radial-gradient(circle, #F9A8D4 0%, transparent 70%)' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full opacity-8" style={{ background: 'radial-gradient(circle, #FBCFE8 0%, transparent 70%)' }} />

        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <div ref={ref4} className={`reveal ${vis4 ? 'visible' : ''}`}>
            <div className="text-center space-y-6 font-body text-lg leading-relaxed" style={{ color: '#3D2E50' }}>
              <p>
                Has practicado.<br />
                Has aprendido.<br />
                Pero todavía dudas mientras diseñas.
              </p>
              <p className="font-semibold text-xl" style={{ color: '#831843' }}>
                No es falta de talento.<br />
                Es falta de una base que confirme cada decisión.
              </p>
              <p>
                El <span className="font-bold" style={{ color: '#9D174D' }}>Manual Cejas Radiantes</span> organiza tu conocimiento
                para que diseñes con estructura, criterio y seguridad real.
              </p>
              <p>
                Incluye materiales de práctica, clases complementarias y acompañamiento por 2 meses para que no avances sola y confirmar que lo estás haciendo correctamente.
              </p>
              <p className="font-semibold text-xl" style={{ color: '#831843' }}>
                Si quieres dejar de practicar con inseguridad
                y empezar a construir estabilidad profesional...
              </p>
              <p className="font-display text-2xl gradient-text font-bold">Este es el siguiente paso.</p>
              <p className="font-display text-xl font-bold" style={{ color: '#BE185D' }}>
                Accede hoy y empieza a diseñar con certeza.
              </p>

              <div className="pt-8">
                <Button onClick={handlePurchase} size="lg" className="cta-pulse rounded-full text-white font-body font-bold text-lg px-14 py-7 h-auto" style={{ backgroundColor: '#DB2777' }}>
                  Quiero Acceder Ahora
                  <ArrowRight size={20} className="ml-2" />
                </Button>
              </div>

              <p className="font-body text-sm italic pt-4" style={{ color: '#9D8BA7' }}>
                *estoy lista para hacer que cada clienta vuelva a mi
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10" style={{ backgroundColor: '#1F1235' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <Heart size={16} style={{ color: '#F472B6' }} />
              <span className="font-display text-lg font-bold" style={{ color: '#F9A8D4' }}>Cejas Radiantes</span>
            </div>
            <div className="flex items-center gap-4">
              <Shield size={14} style={{ color: '#F9A8D4' }} />
              <span className="font-body text-sm" style={{ color: '#D4B5C9' }}>Compra segura · Acceso inmediato · Soporte incluido</span>
            </div>
            <p className="font-body text-xs" style={{ color: '#9D8BA7' }}>
              © {new Date().getFullYear()} Cejas Radiantes. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};
